import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth, useUser, useOrganization, useSession } from '@clerk/clerk-react';
import { Loader } from 'semantic-ui-react';
import { useDispatch } from 'react-redux';

import ClerkLogin from './ClerkLogin';
import entryActions from '../entry-actions';
import Config from '../constants/Config';

/**
 * ClerkAuthWrapper - Integrates Clerk authentication with existing Redux system
 * This component bridges Clerk's auth state with the existing Planka auth flow
 */
const ClerkAuthWrapper = React.memo(({ isInitializing, children }) => {
  const { isLoaded: isClerkLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { session } = useSession();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const dispatch = useDispatch();

  const [isSynced, setIsSynced] = useState(false);
  const prevOrgIdRef = useRef(null);

  console.log(
    `🔄 ClerkAuthWrapper RENDER | Org ID: ${organization?.id} | Loaded: ${isOrgLoaded} | Synced: ${isSynced}`
  );

  useEffect(() => {
    const performSync = async () => {
      if (isSignedIn && user && isOrgLoaded) {
        const currentOrgId = organization?.id;
        const hasOrgChanged = prevOrgIdRef.current !== currentOrgId;
        // Capturar estado inicial antes de actualizar la ref
        const wasInitialLoad = prevOrgIdRef.current === null;

        if (hasOrgChanged || !isSynced) {
          console.log(
            `⚡️ EFFECT TRIGGERED: Org Changed from ${prevOrgIdRef.current} to ${currentOrgId}`
          );

          try {
            const token = await getToken();
            localStorage.setItem('clerkToken', token);

            const userData = {
              id: user.id,
              email: user.primaryEmailAddress?.emailAddress,
              name: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
              username:
                user.username || user.primaryEmailAddress?.emailAddress?.split('@')[0] || 'user',
              organizationId: currentOrgId,
              organizationName: organization?.name,
              clerkUserId: user.id,
            };

            console.log('🚀 Syncing user context with backend (DIRECT CALL)...', userData);

            // 1. Llamada Directa Fetch (Bloqueante)
            const response = await fetch(`${Config.SERVER_BASE_URL}/api/users/clerk-sync`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(userData),
            });

            if (!response.ok) {
              throw new Error(`Sync failed with status: ${response.status}`);
            }

            const data = await response.json();
            console.log('✅ Backend sync completed:', data);

            // 2. Despachar acción de autenticación (para que Redux sepa que estamos listos)
            dispatch(entryActions.authenticate(token));

            // 3. Marcar como sincronizado para desbloquear UI
            prevOrgIdRef.current = currentOrgId;
            setIsSynced(true);

            // 4. Si hubo cambio real de contexto (no inicial), forzar recarga para limpiar Redux
            if (hasOrgChanged && !wasInitialLoad) {
              console.log('🔄 Context switch detected, reloading app...');
              window.location.reload();
            }
          } catch (error) {
            console.error('❌ Error syncing Clerk user:', error);
            // En error, permitimos renderizar para no bloquear al usuario eternamente
            setIsSynced(true);
          }
        }
      }
    };

    if (isClerkLoaded && isSignedIn) {
      performSync();
    }
  }, [
    isClerkLoaded,
    isSignedIn,
    user,
    organization,
    organization?.id,
    isOrgLoaded,
    getToken,
    dispatch,
    session?.lastActiveOrganizationId,
    // Quitamos isSynced de dependencias para evitar loops si cambia dentro
  ]);

  if (!isClerkLoaded || isInitializing || (isSignedIn && !isSynced)) {
    return <Loader active size="massive" />;
  }

  if (!isSignedIn) {
    return <ClerkLogin />;
  }

  return children;
});

ClerkAuthWrapper.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default ClerkAuthWrapper;
